import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Search, 
  Filter, 
  Package, 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown,
  X 
} from "lucide-react";
import { toast } from "sonner";

import { API } from "@/config";

const ItemsPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Filter states
  const [filters, setFilters] = useState({
    sku: "",
    name: "",
    category: "",
    itemType: "",
    unit: ""
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/items`);
      setItems(response.data);
    } catch (error) {
      console.error("Error fetching items:", error);
      toast.error("Failed to fetch items");
    } finally {
      setLoading(false);
    }
  };

  // Get unique values for filter dropdowns
  const uniqueCategories = useMemo(() => [...new Set(items.map(i => i.category))], [items]);
  const uniqueItemTypes = useMemo(() => [...new Set(items.map(i => i.item_type))], [items]);
  const uniqueUnits = useMemo(() => [...new Set(items.map(i => i.unit))], [items]);

  // Filter and search logic
  const filteredItems = useMemo(() => {
    let result = items;

    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item => 
        item.name.toLowerCase().includes(query) ||
        item.sku.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query) ||
        item.item_type.toLowerCase().includes(query)
      );
    }

    // Apply filters
    if (filters.sku) {
      result = result.filter(item => item.sku.toLowerCase().includes(filters.sku.toLowerCase()));
    }
    if (filters.name) {
      result = result.filter(item => item.name.toLowerCase().includes(filters.name.toLowerCase()));
    }
    if (filters.category) {
      result = result.filter(item => item.category === filters.category);
    }
    if (filters.itemType) {
      result = result.filter(item => item.item_type === filters.itemType);
    }
    if (filters.unit) {
      result = result.filter(item => item.unit === filters.unit);
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();
        
        if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [items, searchQuery, filters, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-4 h-4 ml-1 opacity-30" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" /> 
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  const handleRowClick = (item) => {
    navigate(`/items/${item.item_id}`);
  };

  const clearFilters = () => {
    setFilters({
      sku: "",
      name: "",
      category: "",
      itemType: "",
      unit: ""
    });
    setShowFilterModal(false);
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== "");

  if (loading) {
    return (
      <div className="items-page" style={{ padding: '2rem' }}>
        <div className="text-center">Loading items...</div>
      </div>
    );
  }

  return (
    <div className="items-page" style={{ padding: '2rem', background: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: '2rem' }}>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Package className="w-8 h-8" />
            Items
          </h1>
          <Badge variant="outline" className="text-sm">
            {filteredItems.length} of {items.length} items
          </Badge>
        </div>
        
        {/* Search and Filter Controls */}
        <div className="flex gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search items by name, SKU, category, or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button 
            variant={hasActiveFilters ? "default" : "outline"}
            onClick={() => setShowFilterModal(true)}
            className="flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            Filters
            {hasActiveFilters && (
              <Badge variant="secondary" className="ml-1">
                {Object.values(filters).filter(v => v !== "").length}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {/* Items Table */}
      <Card>
        <CardContent className="p-0">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                <tr>
                  <th 
                    style={{ padding: '1rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('sku')}
                  >
                    <div className="flex items-center font-semibold">
                      SKU
                      {getSortIcon('sku')}
                    </div>
                  </th>
                  <th 
                    style={{ padding: '1rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center font-semibold">
                      Item Name
                      {getSortIcon('name')}
                    </div>
                  </th>
                  <th 
                    style={{ padding: '1rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('category')}
                  >
                    <div className="flex items-center font-semibold">
                      Category
                      {getSortIcon('category')}
                    </div>
                  </th>
                  <th 
                    style={{ padding: '1rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('item_type')}
                  >
                    <div className="flex items-center font-semibold">
                      Item Type
                      {getSortIcon('item_type')}
                    </div>
                  </th>
                  <th 
                    style={{ padding: '1rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('unit')}
                  >
                    <div className="flex items-center font-semibold">
                      Unit of Measure
                      {getSortIcon('unit')}
                    </div>
                  </th>
                  <th 
                    style={{ padding: '1rem', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('on_hand')}
                  >
                    <div className="flex items-center justify-end font-semibold">
                      On Hand
                      {getSortIcon('on_hand')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ padding: '3rem', textAlign: 'center', color: '#6c757d' }}>
                      <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No items found</p>
                      {(searchQuery || hasActiveFilters) && (
                        <p className="text-sm mt-2">Try adjusting your search or filters</p>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => (
                    <tr 
                      key={item.item_id}
                      onClick={() => handleRowClick(item)}
                      style={{ 
                        borderBottom: '1px solid #e9ecef',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <td style={{ padding: '1rem' }}>
                        <span className="font-mono text-sm">{item.sku}</span>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div className="font-medium">{item.name}</div>
                        <div className="text-sm text-gray-500">{item.description}</div>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <Badge variant="outline">{item.category}</Badge>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <Badge 
                          variant={item.item_type === 'food' ? 'default' : 'secondary'}
                        >
                          {item.item_type}
                        </Badge>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {item.unit}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'right' }}>
                        <span className="font-semibold">{item.on_hand.toLocaleString()}</span>
                        <span className="text-sm text-gray-500 ml-1">{item.unit}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Filter Modal */}
      <Dialog open={showFilterModal} onOpenChange={setShowFilterModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filter Items
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium mb-2 block">SKU</label>
              <Input
                placeholder="Filter by SKU..."
                value={filters.sku}
                onChange={(e) => setFilters({...filters, sku: e.target.value})}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Item Name</label>
              <Input
                placeholder="Filter by item name..."
                value={filters.name}
                onChange={(e) => setFilters({...filters, name: e.target.value})}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Category</label>
              <select
                value={filters.category}
                onChange={(e) => setFilters({...filters, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">All Categories</option>
                {uniqueCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Item Type</label>
              <select
                value={filters.itemType}
                onChange={(e) => setFilters({...filters, itemType: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">All Types</option>
                {uniqueItemTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Unit of Measure</label>
              <select
                value={filters.unit}
                onChange={(e) => setFilters({...filters, unit: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">All Units</option>
                {uniqueUnits.map(unit => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="flex gap-2 justify-end mt-6">
            <Button variant="outline" onClick={clearFilters}>
              Clear All
            </Button>
            <Button onClick={() => setShowFilterModal(false)}>
              Apply Filters
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ItemsPage;
